import { List } from '@/types';
import { ConnectedAccountType } from '@/types/database/generated/ConnectedAccount';
import { ListDataSource } from '@/types/database/generated/ListDataSource';
import { ListViewColumnDataType } from '@/types/database/generated/ListViewColumn';

import { apiClient } from '../http';
import { favoriteService } from './favorites';
import { toListMutationInput } from './resourceMutationPayloads';
import { createSanitizedResourceService, QueryParams } from './resources';

export type ListMetadata = {
    data: unknown[];
    nextCursor?: unknown;
    total: number;
};

export type ListDataSourceColumnSchema = {
    connectedAccountId: string;
    connectedAccountName: string;
    connectedAccountType?: ConnectedAccountType;
    dataType: ListViewColumnDataType;
    datasetDescription?: string;
    datasetId: string;
    datasetLabel: string;
    fieldKey: string;
    fieldLabel: string;
    key: string;
    label: string;
};

const base = createSanitizedResourceService<
    List,
    ReturnType<typeof toListMutationInput>,
    ReturnType<typeof toListMutationInput>
>('/api/lists', toListMutationInput, toListMutationInput);

export const listService = {
    ...base,
    addDataSource: (listId: string, connectedAccountId: string) =>
        apiClient.post<ListDataSource, { connectedAccountId: string }>(`/api/lists/${listId}/data-sources`, {
            connectedAccountId,
        }),
    getMetadata: <TParams extends QueryParams = QueryParams>(listId: string, params?: TParams) =>
        apiClient.get<ListMetadata, TParams>(`/api/lists/${listId}/metadata`, { params }),
    getDataSourceColumnSchemas: (listId: string) =>
        apiClient.get<ListDataSourceColumnSchema[]>(`/api/lists/${listId}/data-source-columns`),
    list: <TParams extends QueryParams = QueryParams>(params?: TParams) => base.list(params),
    removeDataSource: (listId: string, connectedAccountId: string) =>
        apiClient.delete<void>(`/api/lists/${listId}/data-sources/${connectedAccountId}`),
    setArchived: (id: string, archived: boolean) => base.update(id, { archived }),
    setFavorite: (id: string, favorited: boolean) => favoriteService.set<List>('lists', id, favorited),
    setLocked: (id: string, locked: boolean) => base.update(id, { locked }),
};
